// The question-driven tour (spec §5): every chapter is a bubble in the Guide pane — title, narration, the muted contrast
// line, the question and its answers, Back, a "show me" chip per focus target. The tour is linear (operator 2026-10-16):
// Next, Back and the chapter list are the only ways between chapters, and nothing advances without the visitor.
// Modes: `free` (no tour yet) and `tour`. A tour can be paused: the visitor stepped away to explore (the Plant pane, the
// chat, or closed the Guide). Pausing holds the narration mid-sentence with its highlight, camera cues and timers; the
// Guide button then reads "▶ <chapter>", and resume() carries on from the same word. The chapter is remembered across
// visits (STORAGE_KEYS.chapter).
import { CHAPTERS, WELCOME, TARGET_LABELS, transcriptOf } from "./chapters.js"
import { AUDIO_MODES, readingMs, READING_WPS } from "./audio.js"
import { eventFocus } from "./events-view.js"
import { TIMINGS } from "./audio/timings.js"

export const STORAGE_KEYS = { audio: "xl.batchdemo.audio", seen: "xl.batchdemo.seen", chapter: "xl.batchdemo.chapter" }
/** How often the narration clock is read for the sentence highlight and the camera cues. */
export const TICK_MS = 100

const words = (text) => String(text || "").trim().split(/\s+/).filter(Boolean)
/**
 * The recorded take's word times for a chapter, while the take still says what the chapter says (audio/timings.js is
 * generated with the exact text; an edited script falls back to pacing until it is re-recorded). Null otherwise.
 */
export function voiceTimeline(ch, timings = TIMINGS) {
  const t = timings && timings[ch.id]
  const text = [ch.narration, ch.contrast].filter(Boolean).join(" ")
  return t && t.text === text && Array.isArray(t.starts) && t.starts.length === words(text).length ? t : null
}
/** A sentence's phrases, split after the punctuation the voice pauses on (, ; : and dashes); never empty. */
export function splitClauses(sentence) {
  const parts = String(sentence || "").trim().split(/(?<=[,;:\u2014\u2013])\s+/).filter(Boolean)
  return parts.length ? parts : [String(sentence || "")]
}
/** Index of the first word of each narration phrase (every sentence's clauses, in order), in the word stream. */
function clauseWordIdx(ch) {
  const out = []; let k = 0
  for (const sen of splitSentences(ch.narration)) for (const cl of splitClauses(sen)) { out.push(k); k += words(cl).length }
  return out
}
/** Index of the first word of each narration sentence, in the narration + contrast word stream. */
function sentenceWordIdx(ch) {
  const out = []; let k = 0
  for (const sen of splitSentences(ch.narration)) { out.push(k); k += words(sen).length }
  return out
}
/** Index of the word where `phrase` starts in the narration (the camera cue's word), or -1. */
function phraseWordIdx(ch, phrase) {
  const i = ch.narration.indexOf(phrase)
  return i < 0 ? -1 : words(ch.narration.slice(0, i)).length
}
/**
 * When a camera cue comes in: on its word in the recorded take (timings), else at the chapter's reading-pace `at`
 * (scaled to the clip's length when a clip with no timings plays).
 */
function cueTime(ch, c, pos) {
  const tl = pos && pos.kind === "voice" ? voiceTimeline(ch) : null
  if (tl && c.word >= 0) return tl.starts[Math.min(c.word, tl.starts.length - 1)]
  const at = c.f.at != null ? c.f.at : (c.word >= 0 ? c.word / READING_WPS : 0)
  if (pos && pos.kind === "voice" && pos.dur > 0) {
    const total = words([ch.narration, ch.contrast].join(" ")).length / READING_WPS
    return total > 0 ? at * (pos.dur / total) : at
  }
  return at
}
/**
 * When word `k` of the narration is heard, on the clock `pos` reports: the recorded take's own time for that word when the
 * chapter has timings and a clip is playing; the reading pace otherwise (scaled to the clip's length when a clip with no
 * timings plays, so the highlight still ends with the voice).
 */
export function wordTime(ch, k, pos) {
  const tl = pos && pos.kind === "voice" ? voiceTimeline(ch) : null
  if (tl) return tl.starts[Math.min(k, tl.starts.length - 1)]
  const atRead = k / READING_WPS
  if (pos && pos.kind === "voice" && pos.dur > 0) {
    const total = words([ch.narration, ch.contrast].join(" ")).length / READING_WPS
    return total > 0 ? atRead * (pos.dur / total) : atRead
  }
  return atRead
}
/** Minimum time a chapter stays up on the slow path, however short its text. */
export const AUTO_MIN_DWELL_S = 12
/** Sentences of a narration, for pacing: split after . ! ? followed by a space; never empty. */
export function splitSentences(text) {
  const parts = String(text || "").trim().split(/(?<=[.!?])\s+/).filter(Boolean)
  return parts.length ? parts : [String(text || "")]
}
/**
 * Every chapter's clips (narration, then question) in tour order starting at chapter `from`, then the chapters before it:
 * the order the narration is fetched in, so the chapter the visitor is on never waits behind the rest.
 */
export function tourClipOrder(from = 0, chapters = CHAPTERS) {
  const n = chapters.length, i0 = Math.max(0, Math.min(n - 1, Number(from) || 0)), out = []
  for (let k = 0; k < n; k++) { const c = chapters[(i0 + k) % n]; for (const u of [c.mp3, c.questionMp3]) if (u) out.push(u) }
  return out
}
/**
 * What the Guide says about the narration's download, from audio.status(): null (nothing to say: not narrated, or
 * every clip is in), { kind: "loading", text: "Preparing narration…" } before the first clip has arrived, "Loading
 * narration…" when a later chapter's clip has not, else { kind: "progress", text: "Narration n/N ready" }.
 */
export function narrationStatus(st, { narrated = true, touring = true } = {}) {
  if (!st || !narrated || !touring) return null
  if (st.loading) return { kind: "loading", text: st.ready > 0 ? "Loading narration…" : "Preparing narration…" }
  if (st.total > 0 && st.ready + (st.failed || 0) < st.total) return { kind: "progress", text: `Narration ${st.ready}/${st.total} ready` }
  return null
}
const AUDIO_LABEL = { narrated: "🔊 Narration on", silent: "🔔 Sounds only", none: "🔇 Silent" }
const LIVE_CLASSES = ["line.stopped", "tank.rate_change", "transfer.held"]   // the chapter-2 chain, as the detector reports it

/**
 * @param model    the plant (createModel): setLineRunning, injectFault, tank, units, alarms, t
 * @param trend    the page trend: setWindow, setLive, highlightEvent?
 * @param lens     createLens(): show(focus), hide()
 * @param dock     createDock(): el(tab), open(tab), …; the bubble renders into el('guide')
 * @param audio    createAudio(): setMode, speak({narration, contrast, question, mp3, questionMp3}), stop
 * @param detector createDetector() or null: its events fill the live reveals and the event chips
 */
export function createTour({ model, trend, lens, dock, audio, detector = null, eventsView = null, storage, doc, stats = null }) {
  const $ = (id) => doc.getElementById(id)
  const win = doc.defaultView
  lens = lens || { show() {}, hide() {} }
  const ctx = { model, trend, detector, dock, lens, stats }
  const subs = new Set()
  const tour = {
    mode: "free", chapterIdx: 0, storedAudio: null, paused: false, pausedBy: null,
    /** The tour has something the visitor has not seen (a chapter completed while it was paused): the host may pulse. */
    attention: false,
    init, showWelcome, start, stop, goto, next, back, answer, handleEvent, pause, resume, startOver,
    /** The host shows something on the lens itself: stop the chapter's timed focus from replacing it. */
    holdFocus() { if (v) v.manualFocus = true },
    /** True while this chapter's narration is playing (the chat cues wait for it to finish); a paused tour is quiet. */
    speaking: () => !!(v && (v.speaking || v.answering)) && !tour.paused,
    /** Where the Guide button leads: start the tour, or carry on at the remembered chapter (with its title). */
    resumeTarget() {
      const ch = CHAPTERS[tour.chapterIdx]
      const fresh = tour.mode !== "tour" && !tour.everStarted
      return { kind: fresh ? "start" : "resume", chapterIdx: tour.chapterIdx, title: ch.title }
    },
    everStarted: false,
    onChapter(fn) { subs.add(fn); return () => subs.delete(fn) },
  }
  tour.onChange = tour.onChapter   // v2 name, kept for the host
  const el = {}                    // bubble parts
  let visit = 0, timers = []
  let savedView = null   // the visitor's trend window, held while a savesView chapter (5) has the page trend at 8 h
  let v = fresh()
  function fresh() { return { answered: false, choice: null, waiting: null, auto: false, triggerSeen: false, narrDone: false, voiced: false, dwellDone: false, live: false, manualFocus: false, stopT: null, speaking: false, run: 0, pace: 0, userStopped: false } }

  function safeGet(k) { try { return storage.getItem(k) } catch (_) { return null } }
  function safeSet(k, val) { try { storage.setItem(k, val) } catch (_) {} }
  function notify() { for (const fn of subs) { try { fn(tour) } catch (err) { console.error("tour listener", err) } } }   // a host listener must not stall the tour
  // Timers the chapter owns (reveal → Next, the slow path's auto-advance): they stop while the tour is paused.
  function later(fn, ms) {
    const t = { fn, left: Math.max(0, ms), since: 0, id: null }
    t.arm = () => { t.since = Date.now(); t.id = setTimeout(() => { timers = timers.filter((x) => x !== t); t.fn() }, t.left) }
    timers.push(t)
    if (!tour.paused) t.arm()
    return t
  }
  function clearTimers() { for (const t of timers) clearTimeout(t.id); timers = []; if (ticker) { clearInterval(ticker); ticker = null } }
  function holdTimers() { for (const t of timers) if (t.id != null) { clearTimeout(t.id); t.id = null; t.left = Math.max(0, t.left - (Date.now() - t.since)) } }
  function releaseTimers() { for (const t of timers) if (t.id == null) t.arm() }
  let ticker = null
  const on = (id, fn) => { const e = $(id); if (e) e.addEventListener("click", fn) }

  // ---------- setup ----------
  function init() {
    const stored = safeGet(STORAGE_KEYS.audio)
    // Consent gate: the stored choice is remembered but applied only by a user action (Start / overlay choice).
    tour.storedAudio = AUDIO_MODES.includes(stored) ? stored : null
    // A return visit carries on where the visitor left the tour (the Guide button reads "▶ <that chapter>").
    const sc = Number(safeGet(STORAGE_KEYS.chapter))
    if (safeGet(STORAGE_KEYS.chapter) != null && Number.isInteger(sc) && sc >= 0 && sc < CHAPTERS.length) { tour.chapterIdx = sc; tour.everStarted = true }
    audio.setMode("none")
    on("welcomeNarrated", () => choose("narrated"))
    on("welcomeSilent", () => choose("silent"))
    on("welcomeFree", () => choose("none"))
    on("returnChange", () => { const b = $("returnBanner"); if (b) b.hidden = true; showWelcome() })
    // Touring: start over from chapter 0. Not touring: start (or resume) — with no stored choice, ask (the welcome
    // choice) rather than quietly starting a silent tour.
    on("walkBtn", () => (tour.mode === "tour" ? startOver() : tour.storedAudio ? start(tour.storedAudio) : showWelcome()))
    on("audioModeBtn", () => { const i = AUDIO_MODES.indexOf(audio.mode); setAudio(AUDIO_MODES[(i + 1) % AUDIO_MODES.length]) })
    // While narrated, every tap (Next, Back, an answer, a chapter tab) keeps the browser's permission to play fresh.
    doc.addEventListener("click", () => { if (audio.mode === "narrated") audio.unlock() }, true)
    buildBubble()
    if (typeof audio.onStatus === "function") audio.onStatus(renderNarrStatus)
    buildChapterList()
    renderTranscript()
    if (detector && typeof detector.onDetect === "function") detector.onDetect((ev) => handleEvent(ev))
    if (safeGet(STORAGE_KEYS.seen) === "1") { const b = $("returnBanner"); if (b) b.hidden = false } else showWelcome()
    renderControls()
  }
  function showWelcome() {
    const o = $("welcomeOverlay"); if (!o) return
    o.hidden = false
    const t = o.querySelector("[data-welcome-title]"); if (t) t.textContent = WELCOME.title
    const b = o.querySelector("[data-welcome-body]")
    if (b) {
      // The new last line replaces v2's trend sentence at the end of the paragraph (spec §6).
      b.textContent = WELCOME.body + " "
      const last = doc.createElement("strong"); last.className = "welcome-last"; last.setAttribute("data-welcome-last", ""); last.textContent = WELCOME.last
      b.appendChild(last)
    }
    const c = o.querySelector("[data-welcome-callouts]")
    if (c) { c.textContent = ""; for (const [, text] of WELCOME.callouts) { const li = doc.createElement("li"); li.textContent = text; c.appendChild(li) } }
    const tr = o.querySelector("[data-welcome-transcript]"); if (tr) tr.textContent = transcriptOf(CHAPTERS[0])
  }
  function choose(mode) {
    if (mode === "narrated") audio.unlock()   // inside the tap: the browser allows sound from here on
    safeSet(STORAGE_KEYS.audio, mode); safeSet(STORAGE_KEYS.seen, "1"); tour.storedAudio = mode
    const o = $("welcomeOverlay"); if (o) o.hidden = true
    if (mode === "none") { audio.setMode("none"); stop() } else start(mode)
  }
  function setAudio(m) {
    if (m === "narrated") audio.unlock()
    audio.setMode(m); safeSet(STORAGE_KEYS.audio, m); tour.storedAudio = m
    if (m === "narrated") audio.prefetch(clipUrls(tour.chapterIdx))
    // Turning narration on mid-chapter speaks this chapter now, not from the next one.
    if (m === "narrated" && tour.mode === "tour") narrate(visit, CHAPTERS[tour.chapterIdx])
    renderControls(); notify()
  }
  const clipUrls = (from = 0) => tourClipOrder(from)
  /** The bubble's Listen button: a real button, so the sound always starts from the visitor's own tap. */
  function listen() {
    if (tour.mode !== "tour") return
    if (tour.paused) { resume(); return }
    if (audio.mode === "narrated" && (v.speaking || v.answering)) { v.userStopped = true; audio.stop(); return }
    if (audio.mode !== "narrated") { setAudio("narrated"); return }   // setAudio speaks the chapter
    audio.unlock()
    narrate(visit, CHAPTERS[tour.chapterIdx])
  }
  function renderListen() {
    if (!el.listen) return
    const talking = v.speaking || v.answering
    const label = tour.paused && talking ? ["▶ Continue", "Continue the narration where it paused"]
      : audio.mode !== "narrated" ? ["🔊 Listen", "Play this chapter's narration"]
      : talking ? ["⏹ Stop", "Stop the narration"] : ["🔁 Listen again", "Play this chapter's narration again"]
    el.listen.textContent = label[0]; el.listen.setAttribute("aria-label", label[1])
    el.listen.setAttribute("aria-pressed", String(audio.mode === "narrated" && talking))
  }

  /** The Guide's narration readiness line (see el.narrStatus); `st` is audio.status(). */
  function renderNarrStatus(st) {
    const e = el.narrStatus; if (!e) return
    if (!st) st = typeof audio.status === "function" ? audio.status() : null
    const s = narrationStatus(st, { narrated: audio.mode === "narrated", touring: tour.mode === "tour" })
    e.hidden = !s
    e.className = "gb-narr-status" + (s ? ` ${s.kind}` : "")
    e.textContent = s ? s.text : ""
    // Announce the waits; the counter ticks quietly.
    e.setAttribute("aria-live", s && s.kind === "loading" ? "polite" : "off")
  }
  /** The bubble lives at the top of the Guide tab; #captionText (the v2 caption) becomes its narration line. */
  function buildBubble() {
    if (el.root) return
    const pane = (dock && dock.el && dock.el("guide")) || doc.body
    const host = pane.querySelector(".dock-slot") || pane
    const h = (tag, cls, attrs = {}) => { const e = doc.createElement(tag); if (cls) e.className = cls; for (const [k, val] of Object.entries(attrs)) e.setAttribute(k, val); return e }
    const root = h("section", "guide-bubble", { id: "guideBubble", "aria-live": "polite", "aria-label": "Tour chapter" })
    root.hidden = true
    el.step = h("div", "gb-step")
    el.title = h("h3", "gb-title", { id: "gbTitle" })
    let narr = $("captionText")
    const oldBox = narr && narr.parentElement && narr.parentElement.classList.contains("caption-box") ? narr.parentElement : null
    if (!narr) narr = h("p", "", { id: "captionText" })
    narr.className = "gb-narr"
    el.narr = narr
    el.audioNote = h("p", "gb-audio-note", { hidden: "" }); el.audioNote.hidden = true
    // Narration readiness (kraftware-dev#129): "Preparing narration…" / "Loading narration…" while the chapter's clip is on
    // its way, then a quiet "Narration n/N ready" while the rest load. Silent and explore never show it.
    el.narrStatus = h("p", "gb-narr-status", { id: "gbNarrStatus", "aria-live": "off" }); el.narrStatus.hidden = true
    el.listen = h("button", "btn gb-listen", { id: "gbListen", type: "button" })
    el.listen.addEventListener("click", listen)
    el.more = h("button", "gb-more", { type: "button", "aria-expanded": "false" }); el.more.textContent = "More ▾"   // phone only (CSS)
    el.more.addEventListener("click", () => { const open = !root.classList.contains("gb-open"); root.classList.toggle("gb-open", open); el.more.textContent = open ? "Less ▴" : "More ▾"; el.more.setAttribute("aria-expanded", String(open)) })
    el.contrast = h("p", "gb-contrast", { "aria-label": "What you probably have today" })
    el.question = h("p", "gb-question", { id: "gbQuestion" })
    el.answers = h("div", "gb-answers", { id: "gbAnswers", role: "group", "aria-labelledby": "gbQuestion" })
    el.reveal = h("div", "gb-reveal", { id: "gbReveal", role: "status" }); el.reveal.hidden = true
    const nav = h("div", "gb-nav")
    el.back = h("button", "btn", { id: "gbBack", type: "button" }); el.back.textContent = "← Back"
    el.next = h("button", "btn btn-primary", { id: "gbNext", type: "button" }); el.next.textContent = "Next →"; el.next.hidden = true
    nav.append(el.back, el.next)
    el.chips = h("div", "gb-chips", { "aria-label": "Show me" })
    const head = h("div", "gb-head"); head.append(el.title, el.listen)
    root.append(el.step, head, el.narrStatus, narr, el.audioNote, el.more, el.contrast, el.question, el.answers, el.reveal, nav, el.chips)
    host.insertBefore(root, host.firstChild)
    if (oldBox && !oldBox.children.length) oldBox.remove()
    el.root = root
    el.back.addEventListener("click", back)
    el.next.addEventListener("click", next)
  }
  function buildChapterList() {
    const strip = $("chapterStrip"); if (!strip) return
    strip.textContent = ""
    CHAPTERS.forEach((c, i) => {
      const b = doc.createElement("button"); b.type = "button"; b.className = "ch-tab"; b.dataset.scene = String(i); b.textContent = c.title
      b.addEventListener("click", () => { if (tour.mode !== "tour") show(true); goto(i) })
      strip.appendChild(b)
    })
  }
  function renderTranscript() {
    const all = $("transcriptAll"); if (!all) return
    all.textContent = ""
    for (const c of CHAPTERS) {
      const h4 = doc.createElement("h4"); h4.textContent = c.title
      const p = doc.createElement("p"); p.textContent = c.narration
      const pc = doc.createElement("p"); pc.className = "tr-contrast"; const em = doc.createElement("em"); em.textContent = c.contrast; pc.appendChild(em)
      all.append(h4, p, pc)
      if (c.question) { const pq = doc.createElement("p"); pq.className = "tr-q"; pq.textContent = `Q: ${c.question}`; all.append(pq) }
    }
  }
  function renderControls() {
    const w = $("walkBtn")
    if (w) {
      const r = tour.resumeTarget()
      w.textContent = tour.mode === "tour" ? "↺ Start over" : r.kind === "resume" && r.chapterIdx > 0 ? `▶ Resume · ${r.title}` : "▶ Start the tour"
    }
    const a = $("audioModeBtn"); if (a) a.textContent = AUDIO_LABEL[audio.mode] || AUDIO_LABEL.none
    renderListen()
    renderNarrStatus()
    const strip = $("chapterStrip")
    if (strip) for (const b of strip.querySelectorAll("[data-scene]")) {
      const cur = tour.mode === "tour" && Number(b.dataset.scene) === tour.chapterIdx
      b.classList.toggle("active", cur); if (cur) b.setAttribute("aria-current", "step"); else b.removeAttribute("aria-current")
    }
  }

  // ---------- what the tour owns while touring ----------
  /** The line may be stopped only in chapters 2 and 3 (3 asks "with the line stopped…"); anywhere else the tour restarts it. */
  const LINE_STOP_CHAPTERS = new Set([2, 3])
  function ownLine(i) {
    if (LINE_STOP_CHAPTERS.has(i)) return
    if (!model || !model.tank || model.tank.lineRunning !== false) return
    // Chapter 7's "Stop the filling line" is the tank_high fault: undo the fault (clearing it restarts the line), so
    // Controls does not keep showing it active. A line the visitor stopped by hand just restarts.
    if (model.faults && typeof model.faults.has === "function" && model.faults.has("tank_high") && typeof model.clearFault === "function") model.clearFault("tank_high")
    else if (typeof model.setLineRunning === "function") model.setLineRunning(true)
  }
  function restoreView() {
    if (!savedView) return
    const sv = savedView; savedView = null
    if (trend) { trend.setWindow(sv.window); if (typeof trend.setLive === "function") trend.setLive(sv.live) }
  }

  // ---------- modes ----------
  function show(on) { tour.mode = on ? "tour" : "free"; if (el.root) el.root.hidden = !on }
  /** Start the tour (or carry on): `from` defaults to the remembered chapter, so a return visitor lands where they left. */
  function start(audioMode, from = tour.chapterIdx) {
    if (audioMode === "narrated") audio.unlock()
    if (AUDIO_MODES.includes(audioMode)) audio.setMode(audioMode)
    audio.prefetch(clipUrls(from))   // the chapter about to play first, then the ones after it (audio.js fetches two at a time)
    show(true)
    if (dock && dock.open) dock.open("guide")
    goto(from)
  }
  /** Back to chapter 0 from anywhere ("↺ Start over"). */
  function startOver() { start(audio.mode, 0) }
  /**
   * The visitor stepped away (opened the Plant pane or the chat, or closed the Guide): hold the narration where it is,
   * with its sentence highlight, its camera cues and the chapter's timers. Events still count (a chapter can complete
   * while paused); the Guide button reads "▶ <chapter>" and resume() carries on from the same word.
   */
  function pause(by = "visitor") {
    if (tour.mode !== "tour" || tour.paused) return
    tour.paused = true; tour.pausedBy = by
    audio.pause()
    holdTimers()
    renderListen(); notify()
  }
  /** Back to the tour (call it from the visitor's tap: an iPhone resumes sound only from one). */
  function resume() {
    if (!tour.paused) return
    tour.paused = false; tour.pausedBy = null; tour.attention = false
    audio.resume()
    releaseTimers()
    if (v.lastFocus && !v.manualFocus) lens.show(lensSpec(v.lastFocus))   // the camera goes back to where the chapter had it
    renderListen(); notify()
  }
  function stop() {
    clearTimers(); visit++; v = fresh()
    tour.paused = false; tour.pausedBy = null
    show(false)
    lens.hide()
    clearCallouts()
    ownLine(-1)
    restoreView()
    if (audio.mode === "narrated") audio.stop()
    renderControls(); notify()
  }

  // ---------- chapters ----------
  function goto(i) {
    i = Math.max(0, Math.min(CHAPTERS.length - 1, Number(i) || 0))
    const ch = CHAPTERS[i]
    clearTimers()
    const token = ++visit
    v = fresh()
    tour.chapterIdx = i; tour.everStarted = true
    tour.paused = false; tour.pausedBy = null; tour.attention = false
    safeSet(STORAGE_KEYS.chapter, String(i))
    show(true)
    if (audio.mode === "narrated") audio.stop()
    lens.hide()
    clearCallouts()
    ownLine(i)
    if (ch.savesView) { if (!savedView && trend && typeof trend.view === "function") { const vw = trend.view(); savedView = { window: vw.window, live: vw.live } } }
    else restoreView()   // before onEnter: a chapter that sets its own window wins
    if (ch.onEnter) ch.onEnter(ctx)
    renderBubble(ch)
    renderControls()
    applyFocus(ch)
    narrate(token, ch)
    later(() => { if (token === visit) { v.dwellDone = true; check(token) } }, AUTO_MIN_DWELL_S * 1000)
    notify()
  }
  /**
   * Speak (or, unvoiced, pace) this chapter. Listen again and turning narration on call it again within the same visit:
   * each call is a run, and only the newest run's result counts.
   */
  function narrate(token, ch) {
    if (token !== visit) return
    if (tour.paused) { tour.paused = false; tour.pausedBy = null; releaseTimers() }   // playing again ends a pause
    if (audio.paused) audio.stop()
    const run = ++v.run
    v.userStopped = false; v.speaking = audio.mode === "narrated"
    if (el.audioNote) { el.audioNote.hidden = true; el.audioNote.textContent = "" }
    followClock(token, ch)
    renderListen()
    const done = (r) => {
      if (token !== visit || run !== v.run) return
      v.speaking = false; v.narrDone = true; v.voiced = v.voiced || !!(r && r.voiced)
      renderListen(); check(token)
    }
    audio.speak({ narration: ch.narration, contrast: ch.contrast, question: ch.question, mp3: ch.mp3, questionMp3: ch.questionMp3 })
      .then(done, () => done(null))
  }
  /**
   * Follow the narration clock (audio.position(), every TICK_MS): light the sentence being spoken and bring each camera
   * cue in on its word — on the recorded take's own timings (audio/timings.js) when they match, else at reading pace.
   * The clock stops while the tour is paused, so the highlight and the cues hold with the voice.
   */
  function followClock(token, ch) {
    const spans = el.narr ? Array.from(el.narr.querySelectorAll(".gb-sent")) : []
    const clauses = el.narr ? Array.from(el.narr.querySelectorAll(".gb-cl")) : []
    const sentIdx = sentenceWordIdx(ch)
    const clauseIdx = clauseWordIdx(ch)
    const cues = ch.focus.filter((f) => f.cue || f.at).map((f) => ({ f, word: f.cue ? phraseWordIdx(ch, f.cue) : -1 }))
    const fired = new Set()
    const pace = ++v.pace   // a replay restarts the highlight; the older run goes quiet
    const light = (k) => { spans.forEach((o, j) => o.classList.toggle("on", j === k)) }
    // Within the lit sentence, the phrase being spoken: it moves on at each , ; : or dash, with the voice's own pauses.
    const now = (k) => { clauses.forEach((o, j) => o.classList.toggle("now", j === k)) }
    if (spans.length) light(0)   // the first sentence lights with the chapter, not a tick later
    if (clauses.length) now(0)
    let narrationOver = false
    function tick() {
      if (token !== visit || pace !== v.pace) return
      const pos = audio.position()
      if (pos.part === 0) {
        let k = 0
        for (let j = 1; j < sentIdx.length; j++) if (wordTime(ch, sentIdx[j], pos) <= pos.t + 1e-6) k = j
        if (spans.length) light(k)
        let q = 0
        for (let j = 1; j < clauseIdx.length; j++) if (wordTime(ch, clauseIdx[j], pos) <= pos.t + 1e-6) q = j
        if (clauses.length) now(q)
        const readEnd = words(ch.narration).length / READING_WPS
        if (pos.kind !== "voice" && pos.t >= readEnd) { spans.forEach((o) => o.classList.remove("on")); clauses.forEach((o) => o.classList.remove("now")) }
        for (const c of cues) {
          if (fired.has(c)) continue
          if (pos.t + 1e-6 >= cueTime(ch, c, pos)) { fired.add(c); cueFocus(c.f) }
        }
      } else if (!narrationOver && (pos.part === 1 || v.narrDone)) {
        narrationOver = true
        spans.forEach((o) => o.classList.remove("on")); clauses.forEach((o) => o.classList.remove("now"))
        const left = cues.filter((c) => !fired.has(c))   // a narration cut short: its last cue still lands
        for (const c of left) fired.add(c)
        if (left.length) cueFocus(left.at(-1).f)
      }
    }
    if (ticker) clearInterval(ticker)
    ticker = setInterval(tick, TICK_MS)
  }
  function cueFocus(f) {
    v.lastFocus = f
    if (tour.mode === "tour" && !tour.paused && !v.manualFocus && autoShowable(f)) lens.show(lensSpec(f))
  }
  function next() { if (tour.chapterIdx < CHAPTERS.length - 1) goto(tour.chapterIdx + 1) }
  function back() { if (tour.chapterIdx > 0) goto(tour.chapterIdx - 1) }

  /** After the narration (heard or read): reveal Next; nothing advances without the visitor (operator ruling 2026-10-15). */
  function check(token) {
    if (token !== visit || tour.mode !== "tour" || v.answered) return
    const ch = CHAPTERS[tour.chapterIdx]
    if (!v.narrDone) return
    // The tour never moves on by itself: once the narration has been read or heard, Next appears and the visitor
    // decides (operator, 2026-10-15: "wait for the user to make a selection at each chapter"). Unattended viewers
    // simply stay on the chapter; the question is the invitation.
    if (el.next.hidden) { el.next.hidden = false; keepInView(el.next.parentElement) }
    if (audio.mode === "narrated" && !v.voiced && !v.userStopped && el.audioNote) {
      const err = typeof audio.lastError === "function" ? audio.lastError() : null
      el.audioNote.textContent = err ? `Narration could not play (${err.why}${err.t != null ? ` at ${Number(err.t).toFixed(1)} s` : ""}). Reading mode.` : "Narration could not play. Reading mode."
      el.audioNote.hidden = false
    }
  }

  function renderBubble(ch) {
    const i = CHAPTERS.indexOf(ch)
    el.step.textContent = `Guided tour · ${i + 1} of ${CHAPTERS.length}`
    el.title.textContent = ch.title
    el.narr.textContent = ""
    for (const sentence of splitSentences(ch.narration)) {
      const sp = doc.createElement("span"); sp.className = "gb-sent"
      for (const clause of splitClauses(sentence)) { const c = doc.createElement("span"); c.className = "gb-cl"; c.textContent = clause + " "; sp.appendChild(c) }
      el.narr.appendChild(sp)
    }
    el.root.classList.remove("gb-open"); el.more.textContent = "More ▾"; el.more.setAttribute("aria-expanded", "false")
    el.contrast.textContent = ch.contrast
    el.question.textContent = ch.question
    el.question.hidden = !ch.question
    el.answers.textContent = ""
    ch.answers.forEach((a, j) => {
      const link = (a.action || "").startsWith("link:")
      const opt = doc.createElement("div"); opt.className = "gb-opt"
      const b = doc.createElement(link ? "a" : "button")
      b.className = "btn gb-answer"; b.dataset.answer = String(j); b.textContent = a.text
      if (link) { b.href = a.action.slice(5); b.target = "_blank"; b.rel = "noopener" }
      else { b.type = "button"; b.addEventListener("click", () => answer(j)) }
      opt.appendChild(b)
      if (a.why) { const w = doc.createElement("p"); w.className = "gb-why"; w.dataset.why = String(j); w.textContent = a.why; w.hidden = true; opt.appendChild(w) }
      el.answers.appendChild(opt)
    })
    el.reveal.hidden = true; el.reveal.className = "gb-reveal"; el.reveal.textContent = ""
    if (el.audioNote) { el.audioNote.hidden = true; el.audioNote.textContent = "" }
    el.next.hidden = true
    el.back.disabled = i === 0
    renderChips(ch)
    const pane = el.root.closest(".dock-panes"); if (pane) pane.scrollTop = 0   // a new chapter starts at its title
  }
  /** Scroll the dock pane (not the page) just enough that `node` is in view: the reveal and Next on a phone sheet. */
  function keepInView(node) {
    const pane = node && node.closest && node.closest(".dock-panes"); if (!pane || !node.getBoundingClientRect) return
    const n = node.getBoundingClientRect(), p = pane.getBoundingClientRect()
    if (!p.height) return
    const pad = parseFloat((win.getComputedStyle && win.getComputedStyle(pane).paddingBottom) || 0) || 0
    const over = n.bottom - (p.bottom - pad)
    if (n.top < p.top) pane.scrollTop -= p.top - n.top + 8
    else if (over > 0) pane.scrollTop += over
  }
  /** Scroll the dock pane (not the page) so `node` starts at its top: the question and its answer cards after a pick. */
  function toTop(node) {
    const pane = node && node.closest && node.closest(".dock-panes"); if (!pane || !node.getBoundingClientRect) return
    const n = node.getBoundingClientRect(), p = pane.getBoundingClientRect()
    if (!p.height) return
    pane.scrollTop += n.top - p.top - 8
  }
  /** "Your turn": open the dock tab that holds the control, bring it into view and outline it until the chapter moves on. */
  function callOut(id) {
    const c = $(id); if (!c) return
    const pane = c.closest("[data-pane]")
    if (pane && dock && dock.open) dock.open(pane.dataset.pane)
    c.classList.add("tour-callout")
    keepInView(c)
  }
  function clearCallouts() { for (const c of doc.querySelectorAll(".tour-callout")) c.classList.remove("tour-callout") }
  function renderChips(ch) {
    el.chips.textContent = ""
    for (const f of ch.focus) {
      const b = doc.createElement("button"); b.type = "button"; b.className = "chip gb-chip"
      b.textContent = `Show me: ${TARGET_LABELS[f.target] || f.target}`
      b.addEventListener("click", () => { v.manualFocus = true; showFocus(f) })
      el.chips.appendChild(b)
    }
    const ev = refEvent(ch)
    if (ev) {
      const b = doc.createElement("button"); b.type = "button"; b.className = "chip gb-chip gb-chip-event"
      b.textContent = "Show me the detected event"
      b.addEventListener("click", () => { v.manualFocus = true; showEvent(ev) })
      el.chips.appendChild(b)
    }
  }

  // ---------- focus ----------
  function visible(e) {
    if (!e) return false
    if (typeof e.checkVisibility === "function") return e.checkVisibility()
    return !(e.hidden || e.closest("[hidden]"))
  }
  const inDock = (e) => !!(e && e.closest && e.closest("#dock, .dock"))
  function autoShowable(f) {
    if (f.target.startsWith("lane:")) return true
    const e = $(f.target)
    return visible(e) && !inDock(e)
  }
  const lensSpec = (f) => { const { at: _at, ...rest } = f; return rest }
  /** A target inside the dock (the fault buttons) opens its tab; anything else opens the lens. */
  function showFocus(f) {
    if (!f.target.startsWith("lane:")) {
      const e = $(f.target)
      if (inDock(e)) { const pane = e.closest("[data-pane]"); if (pane && dock && dock.open) dock.open(pane.dataset.pane); return }
    }
    lens.show(lensSpec(f))
  }
  /** The chapter's first untimed target goes on the lens at once; the timed ones come in on their words (followClock). */
  function applyFocus(ch) {
    const first = ch.focus.find((f) => !f.at && !f.cue && autoShowable(f))
    if (first) { lens.show(lensSpec(first)); v.lastFocus = first }
  }
  function refEvent(ch) {
    if (!ch.eventRef || !detector || !detector.events) return null
    for (let k = detector.events.length - 1; k >= 0; k--) if (detector.events[k].class === ch.eventRef) return detector.events[k]
    return null
  }
  function showEvent(ev) {
    lens.show(eventFocus(ev, model && Number.isFinite(model.t) ? model.t : undefined))
    if (trend && typeof trend.highlightEvent === "function") trend.highlightEvent(ev.id)
    if (eventsView && eventsView.open) eventsView.open(ev.id)   // the Events tab card opens too, so the story is there when the tab is
  }

  // ---------- answers ----------
  function answer(j, { auto = false } = {}) {
    if (tour.mode !== "tour") return
    const ch = CHAPTERS[tour.chapterIdx], a = ch.answers[j]
    if (!a) return
    const act = a.action || ""
    // Routes leave the bubble as it is: they are never "answered". There are no jumps between chapters (a linear tour).
    if (act === "chat") { if (typeof tour.openChat === "function") tour.openChat({ reason: "tour-answer" }); return }   // the chat bubble (host sets openChat)
    if (act === "free") { pause("free"); if (dock && dock.open) dock.open("controls"); return }   // explore; ▶ on the Guide button returns
    if (act.startsWith("link:")) { try { win.open(act.slice(5), "_blank", "noopener") } catch (_) {} return }
    if (v.answered && !(v.waiting && a.completeOn === v.waiting)) return   // one answer per visit (switching while waiting is fine)
    const token = visit
    v.answered = true; v.choice = j; v.auto = v.auto || auto
    markChosen(j, !!a.completeOn)
    run(act)
    if (a.completeOn) {
      v.waiting = a.completeOn; v.stopT = model ? model.t : null
      setReveal("wait", act === "stop_line" ? "Stopping the filling line…" : "Your turn: Controls tab → ⏹ Stop filling line. I'll pick it up from there.")
      if (a.control) callOut(a.control)
      if (alreadyTrue(a.completeOn)) complete(token)
      return
    }
    const right = a.correct === true || !ch.answers.some((x) => x.correct)
    const text = revealText(ch, right)
    showWhys(j)
    setReveal(right ? "right" : "wrong", text)
    // The answers hold (operator 2026-10-16: "they disappear too fast for me to read"): the reveal stays, with every
    // option's why, until the visitor presses Next. Nothing advances on its own. On a phone sheet, the question and its
    // answer cards come to the top of the pane (the reading), with the reveal and Next below them.
    el.next.hidden = false
    toTop(ch.answers.some((x) => x.why) ? el.question : el.next.parentElement)
    if (v.auto) later(() => { if (token === visit) next() }, readingMs(text))
    speakAnswer(token, ch)
  }
  /**
   * Teach with the answers: in a quiz every option shows ✓ (the right one) or ✗ (the visitor's wrong pick) and its why;
   * for an action (chapter 7's faults) the chosen one says what to watch.
   */
  function showWhys(j) {
    const ch = CHAPTERS[tour.chapterIdx], quiz = ch.answers.some((a) => a.correct)
    for (const b of el.answers.querySelectorAll(".gb-answer")) {
      const k = Number(b.dataset.answer), a = ch.answers[k]
      b.classList.toggle("correct", quiz && !!a.correct)
      b.classList.toggle("wrong", quiz && k === j && !a.correct)
      b.classList.toggle("dim", quiz && k !== j && !a.correct)
      const w = el.answers.querySelector(`.gb-why[data-why="${k}"]`)
      if (w) w.hidden = !(quiz || k === j)
    }
  }
  /** Narrated: the host (Hermes) says the right answer and why, after the visitor's pick, like a quiz show. */
  function speakAnswer(token, ch) {
    if (audio.mode !== "narrated" || !ch.answerText) return
    // The answer take replaces whatever was still being said: the narration's highlight stops with it (its clock is gone).
    v.pace++
    if (el.narr) for (const o of el.narr.querySelectorAll(".gb-sent.on, .gb-cl.now")) o.classList.remove("on", "now")
    v.answering = true; renderListen()
    const done = () => { if (token === visit) { v.answering = false; renderListen() } }
    audio.speak({ narration: ch.answerText, mp3: ch.answerMp3 }).then(done, done)
  }
  function run(act) {
    if (!act) return
    if (act === "stop_line") { if (model && model.setLineRunning) model.setLineRunning(false) }
    else if (act.startsWith("inject:")) { if (model && model.injectFault) model.injectFault(act.slice(7)) }
    else if (act.startsWith("window:")) { if (trend) { trend.setWindow(Number(act.slice(7))); if (trend.setLive) trend.setLive(true) } }
  }
  /**
   * The chosen answer is marked and locked, and so are its siblings — except routes (End's chat and link), which always
   * stay open, and, while a completeOn answer waits, the other completeOn answers ("Do it for me").
   */
  function markChosen(j, keepWaitingOpen) {
    const answers = CHAPTERS[tour.chapterIdx].answers
    for (const b of el.answers.querySelectorAll(".gb-answer")) {
      const k = Number(b.dataset.answer), a = answers[k]
      b.classList.toggle("chosen", k === j)
      if (b.tagName === "BUTTON") b.disabled = k === j || !(isRoute(a) || (keepWaitingOpen && a.completeOn))
    }
  }
  const isRoute = (a) => /^(chat$|free$|link:)/.test(a.action || "")
  function revealText(ch, right) {
    const r = right ? ch.reveal.right : ch.reveal.wrong
    return typeof r === "function" ? r(ctx) : r
  }
  function setReveal(kind, text) {
    el.reveal.hidden = false
    el.reveal.className = `gb-reveal ${kind}`
    el.reveal.textContent = text
    keepInView(el.next.hidden ? el.reveal : el.next.parentElement)
  }
  const matches = (e, name) => !!e && ((e.type === "alarm.raised" && e.id === name) || e.class === name)
  function alreadyTrue(name) {
    if (model && model.alarms && typeof model.alarms.has === "function" && model.alarms.has(name)) return true
    return !!(detector && (detector.events || []).some((e) => e.class === name && e.open))
  }
  /** completeOn arrived: the reveal, with live values and what the detector saw, then Next (or the slow path's timer). */
  function complete(token) {
    if (token !== visit || !v.waiting) return
    v.waiting = null; v.live = true
    clearCallouts()
    const answers = CHAPTERS[tour.chapterIdx].answers
    for (const b of el.answers.querySelectorAll(".gb-answer")) if (b.tagName === "BUTTON") b.disabled = !isRoute(answers[Number(b.dataset.answer)])
    el.next.hidden = false
    renderLive()
    // Back to the bubble for the reveal — unless the visitor paused the tour to explore: then the Guide button asks.
    if (tour.paused) { tour.attention = true; notify() } else if (dock && dock.open) dock.open("guide")
    if (v.auto) later(() => { if (token === visit) next() }, readingMs(el.reveal.textContent))
  }
  function renderLive() {
    const ch = CHAPTERS[tour.chapterIdx]
    setReveal("right", revealText(ch, true))
    if (!detector || !detector.events) return
    const since = v.stopT == null ? -Infinity : v.stopT - 5
    const seen = detector.events.filter((e) => LIVE_CLASSES.includes(e.class) && e.t0 >= since)
    if (!seen.length) return
    const ul = doc.createElement("ul"); ul.className = "gb-evidence"
    for (const e of seen.slice(-4)) { const li = doc.createElement("li"); li.textContent = e.interpretation; ul.appendChild(li) }
    el.reveal.appendChild(ul)
  }

  // ---------- events ----------
  /** Plant events (model.onEvent) and detected events (detector.onDetect) both come through here. */
  function handleEvent(e) {
    if (tour.mode !== "tour" || !e) return
    if (v.waiting && matches(e, v.waiting)) { complete(visit); return }
    if (v.live) renderLive()
    const ch = CHAPTERS[tour.chapterIdx]
    if (e.class && ch.eventRef === e.class && !el.chips.querySelector(".gb-chip-event")) renderChips(ch)
    if (!v.answered && ch.trigger && !v.triggerSeen && e.type && ch.trigger(e, ctx)) { v.triggerSeen = true; check(visit) }
  }

  return tour
}
